import { useEffect, useState } from 'react';
import type { ActionMenuItem } from '@/components/Atoms/FaceliftActionMenu';
import FaceliftSimpleMenu from '@/components/Atoms/FaceliftSimpleMenu';
import { useTutorialActive } from '@/hooks/tutorialsHooks/useTutorialActive';
import { useTourContext } from '@/store/tourContext';
import type { TourElementKey } from '@/utils/tutorials';

// Global flag to track when menu should be forced open for tutorial
let shouldForceOpenMenuForTutorial = false;

// Function to set the flag (called by tutorial action)
// eslint-disable-next-line react-refresh/only-export-components
export const setForceOpenMenuForTutorial = (force: boolean) => {
	shouldForceOpenMenuForTutorial = force;
};

interface ActionMenuWithTutorialSupportProps {
	actionItems: ActionMenuItem[];
	selectedRows: unknown[];
}

/**
 * Component to wrap FaceliftSimpleMenu with tutorial support
 * Automatically opens the menu when tutorial is active and registers elements for tour highlighting
 */
const ActionMenuWithTutorialSupport = ({
	actionItems,
	selectedRows,
}: ActionMenuWithTutorialSupportProps) => {
	const { registerElement } = useTourContext();
	const { 
		isAnyInvoiceOrOnboardingTutorialActive: isAnyInvoiceTutorialActive,
	} = useTutorialActive();

	// Force menu open only when flag is set and tutorial is active
	const [forceOpen, setForceOpen] = useState(false);

	// Check flag periodically when tutorial is active
	useEffect(() => {
		if (!isAnyInvoiceTutorialActive) {
			setForceOpen(false);
			// Reset flag when tutorial ends
			shouldForceOpenMenuForTutorial = false;
			return;
		}

		const checkFlag = () => {
			// During tutorial, force open menu even if no rows are selected
			if (shouldForceOpenMenuForTutorial) {
				setForceOpen(true);
			} else {
				setForceOpen(false);
			}
		};

		checkFlag();
		const interval = setInterval(checkFlag, 100);

		return () => {
			clearInterval(interval);
			// Reset flag when component unmounts or tutorial ends
			shouldForceOpenMenuForTutorial = false;
		};
	}, [isAnyInvoiceTutorialActive, selectedRows.length]);

	// Register all action menu items when menu is open
	useEffect(() => {
		if (!forceOpen) {
			// Clear all registrations when menu is closed
			registerElement('InvoicesAddToCartAction', null);
			registerElement('InvoicesActionViewRef', null);
			registerElement('InvoicesActionDenialsRef', null);
			return;
		}

		// Helper function to find and register a menu item by its text
		const findAndRegisterMenuItem = (
			textToFind: string,
			referenceName: TourElementKey,
		): void => {
			const allElements = document.querySelectorAll('*');
			let foundElement: HTMLElement | null = null;

			for (const element of Array.from(allElements)) {
				const text = element.textContent?.trim();
				if (text === textToFind) {
					const htmlElement = element as HTMLElement;
					const style = window.getComputedStyle(htmlElement);
					const rect = htmlElement.getBoundingClientRect();

					if (
						style.display !== 'none' &&
						style.visibility !== 'hidden' &&
						rect.width > 0 &&
						rect.height > 0
					) {
						foundElement = htmlElement;
						break;
					}
				}
			}

			if (foundElement) {
				registerElement(referenceName, foundElement);
			} else {
				registerElement(referenceName, null);
			}
		};

		// Wait for menu to render, then find and register all menu items
		// Try multiple times to ensure items are found
		const registerMenuItems = () => {
			// Register "Add to Cart" action
			findAndRegisterMenuItem('Add to Cart', 'InvoicesAddToCartAction');

			// Register "View Invoice(s)" action
			findAndRegisterMenuItem('View Invoice(s)', 'InvoicesActionViewRef');

			// Register "Denial Support" action
			findAndRegisterMenuItem('Denial Support', 'InvoicesActionDenialsRef');
		};

		// Try immediately and multiple times
		registerMenuItems();
		const timeoutId1 = setTimeout(registerMenuItems, 200);
		const timeoutId2 = setTimeout(registerMenuItems, 500);
		const timeoutId3 = setTimeout(registerMenuItems, 1000);
		const intervalId = setInterval(registerMenuItems, 1500);

		return () => {
			clearTimeout(timeoutId1);
			clearTimeout(timeoutId2);
			clearTimeout(timeoutId3);
			clearInterval(intervalId);
		};
	}, [forceOpen, registerElement, selectedRows.length]);

	return <FaceliftSimpleMenu actionItems={actionItems} forceOpen={forceOpen} />;
};

export default ActionMenuWithTutorialSupport;
